import * as React from 'react'
import { Text, TouchableOpacity, View, FlatList, Platform } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'
import Root from '@components/root/Root'
import Header from '@components/header/Header'
import IncidentCard from '@components/incidentCard/IncidentCard'
import SyncCard from '@components/incidentCard/SyncCard'
import Drawer from '@components/drawer/DrawerComponent'
import Button from '@components/button/Button'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import { Viewer, ViperPin, ViperViewProps } from '@components/plan/ViewerComponent'
import SimpleAlert from '@components/modal/SimpleAlert'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import { EtatDesLieuxTypeEnum } from '@api/enum'

import useReducer from '@store/useReducer'
import * as EDLStore from '@store/edl/edl'
import * as SiteStore from '@store/site/site'
import * as LocationStore from '@store/location/location'
import * as IncidentStore from '@store/incident/incident'
import * as PlanStore from '@store/plan/plan'
import useSyncStore from '@store/useSyncStore'

import { getMapPins, findLocalisationSubLevels } from '@screens/incident/utils'
import { findManifestation, findTenant, filterIncidentsBySpace } from '../utils'
import { paramsToSearch } from '@api/utils'

const DEFAULT_INIT: ViperViewProps['initialPosition'] = {
  center: [2.273982551847581, 48.82340600971497],
  zoom: 14,
}

interface Props {
  id: string
  spaceId: string
}

const EDLMapScreen = ({ id, spaceId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const scroll = React.useRef<FlatList>(null)

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)

  const form = useReducer(EDLStore.store, (s) => s.form)
  const forms = useReducer(EDLStore.store, (s) => s.forms)
  const EDLs = useReducer(EDLStore.store, (s) => s.EDLs)
  const places = useReducer(LocationStore.store, (s) => s.places)
  const plans = useReducer(PlanStore.store, (s) => s.plans)

  const plan = React.useMemo(() => {
    const current = plans.find((p) => p.localisationId === spaceId)
    if (!!current && !!current.viewers.geojson) {
      return current.viewers.geojson
    }
    return undefined
  }, [spaceId])

  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)
  const [, toSync] = useSyncStore()

  const [loading, setLoading] = React.useState(true)
  const [tab, setTab] = React.useState<'new' | 'entry'>('new')
  const [open, setOpen] = React.useState(true)
  const [selected, setSelected] = React.useState('')

  const space = places.find((p) => p.id === spaceId)
  const subs = [spaceId, ...findLocalisationSubLevels(places, space).map((pl) => pl.id)]
  const isClosed = form.espacesCloturesIds.includes(spaceId)

  const manif = React.useMemo(
    () => findManifestation(manifestations, form.manifestationId),
    [manifestations, form.manifestationId]
  )
  const tenant = React.useMemo(
    () => findTenant(tenants, form.locatairePermanentId),
    [tenants, form.locatairePermanentId]
  )

  // entries edl if exists
  const entries = EDLs.filter(
    (edl) =>
      (edl.manifestationId === form.manifestationId || edl.locatairePermanentId === form.locatairePermanentId) &&
      edl.type === EtatDesLieuxTypeEnum.ENTREE
  )

  // space related incidents id
  const spaceIncidentIds = form.incidentIds
    ? filterIncidentsBySpace(places, incidents, form.incidentIds.incidentIds, [spaceId])
    : []

  // incidents from previous edl (empty incident if entry edl)
  const entriesIncidents =
    form.type === EtatDesLieuxTypeEnum.SORTIE
      ? incidents
          .filter(
            (inc) =>
              inc.localisationId &&
              subs.includes(inc.localisationId) &&
              !!entries.find((edl) => edl.incidentAccepteIds.includes(inc.id))
          )
          .sort((a, b) => b.dateDeCreation.localeCompare(a.dateDeCreation))
      : []

  // new incidents (all incident if entry edl)
  const newIncidents = spaceIncidentIds.sort((a, b) => b.dateDeCreation.localeCompare(a.dateDeCreation))

  // new / entries filter
  const edlIncidents = tab === 'new' ? newIncidents : entriesIncidents
  const pins: ViperPin[] = React.useMemo(() => getMapPins(edlIncidents), [tab])

  const toSyncPins = toSync
    .filter((s) => !!s.incident.plan && !!s.incident.plan.x && !!s.incident.plan.y)
    .map((s) => ({
      id: s.id,
      x: parseFloat(s.incident.plan!.x),
      y: parseFloat(s.incident.plan!.y),
      color: theme.colors.disable,
    }))

  const tabs: { label: string; value: 'new' | 'entry' }[] = [
    { label: i18n.t('screens.edlMap.newIncidents', { number: newIncidents.length }), value: 'new' },
    { label: i18n.t('screens.edlMap.entryIncidents', { number: entriesIncidents.length }), value: 'entry' },
  ]

  const name = !!manif ? manif.libelle : !!tenant ? tenant.tenant.nomLocatairePermanent : ''

  const data = [
    {
      label: name,
      value: 'ORGA',
    },
    {
      label: i18n.t('screens.account.generalSection.pickerOptions.Viparis'),
      value: 'VIPARIS',
    },
  ]

  React.useEffect(() => {
    setTimeout(() => setLoading(false), 500)
  }, [])

  React.useEffect(() => {
    if (selected) {
      const index = edlIncidents.findIndex((i) => i.id === selected)

      if (scroll.current && index > -1) {
        scroll.current.scrollToIndex({ animated: true, index })
      }
    }
  }, [selected, scroll, edlIncidents])

  React.useEffect(() => {
    const updateForms = forms.map((f) => (f.id === form.id && f.type === form.type ? form : f))
    EDLStore.actions.setForms(updateForms)
  }, [form])

  const headerLinks = [
    { label: i18n.t(`screens.site.sites.${site.codeLieu}`), onClick: () => navigation.push('/') },
    { label: i18n.t(`screens.edl.title`), onClick: () => navigation.push(`/edl`) },
    {
      label: name,
      onClick: () =>
        navigation.push(`/edl/spaces/${id}`, {
          manifestationId: form.manifestationId,
          tenantId: form.locatairePermanentId,
        }),
    },
  ]

  const headerButtons: HeaderButton[] = [
    {
      label: i18n.t(`screens.edlMap.${isClosed ? 'reOpenSpace' : 'closeSpace'}`),
      onClick: () => closeSpace(),
      color: 'light',
    },
    {
      label: i18n.t('screens.edlMap.declare'),
      onClick: () =>
        navigation.push(`/incident/start?manifestationId=${form.manifestationId}&edlId=${form.id}&spaceId=${spaceId}`),
      color: 'default',
    },
  ]

  const closeSpace = () => {
    if (isClosed) {
      EDLStore.actions.setForm({ espacesCloturesIds: form.espacesCloturesIds.filter((s) => s !== spaceId) })
    } else {
      EDLStore.actions.setForm({ espacesCloturesIds: [...form.espacesCloturesIds, spaceId] })
      setTimeout(() => {
        if (isPhone) {
          navigation.goBack()
        } else {
          navigation.push(`/edl/spaces/${form.id}?type=${form.type}`)
        }
      }, 500)
    }
  }

  const onItemAction = (id: string) => {
    setSelected('')
    const accepted = form.incidentAcceptIds.includes(id)

    if (!accepted) {
      // switch from refused to accepted
      EDLStore.actions.setForm({
        incidentRefuseIds: form.incidentRefuseIds.filter((i) => i !== id),
        incidentAcceptIds: [...form.incidentAcceptIds, id],
      })
    } else {
      // switch from accepted to refused
      EDLStore.actions.setForm({
        incidentAcceptIds: form.incidentAcceptIds.filter((i) => i !== id),
        incidentRefuseIds: [...form.incidentRefuseIds, id],
      })
    }
  }

  const onAffect = (id: string, value: 'ORGA' | 'VIPARIS') => {
    setSelected('')

    if (value === 'ORGA') {
      // switch from orga to viparis
      EDLStore.actions.setForm({
        incidentViparisIds: form.incidentViparisIds.filter((i) => i !== id),
        incidentOrganisateurIds: [...form.incidentOrganisateurIds, id],
      })
    } else {
      // switch from viparis to orga
      EDLStore.actions.setForm({
        incidentOrganisateurIds: form.incidentOrganisateurIds.filter((i) => i !== id),
        incidentViparisIds: [...form.incidentViparisIds, id],
      })
    }
  }

  const renderItem = ({ item }: { item: IncidentResponse | SyncIncident }) => {
    const accepted = form.incidentAcceptIds.includes(item.id)
    const action: IconName = accepted ? 'unvisibility' : 'visibility'

    const isSync = !!(item as SyncIncident).incident
    const isNew = !!form.newIds && form.newIds.includes(item.id)

    if (isSync) {
      return (
        <IncidentContainer
          activeOpacity={0.9}
          selected={isNew || item.id === selected}
          onPress={() => navigation.push(`/incidentDetail/${item.id}?edlId=${form.id}`)}>
          <SyncCard
            sync={item as SyncIncident}
            action={action}
            onAction={
              form.type === EtatDesLieuxTypeEnum.ENTREE || tab === 'new' ? () => onItemAction(item.id) : undefined
            }
            onAffect={
              form.type === EtatDesLieuxTypeEnum.SORTIE && tab === 'new' ? (val) => onAffect(item.id, val) : undefined
            }
            disabled={!accepted && tab === 'new'}
            data={data}
            dataSelected={form.incidentOrganisateurIds.includes(item.id) ? data[0] : data[1]}
          />
        </IncidentContainer>
      )
    }

    return (
      <IncidentContainer
        activeOpacity={0.9}
        selected={isNew || item.id === selected}
        onPress={() => navigation.push(`/incidentDetail/${item.id}?edlId=${form.id}`)}>
        <IncidentCard
          incident={item as IncidentResponse}
          action={action}
          onAction={
            form.type === EtatDesLieuxTypeEnum.ENTREE || tab === 'new' ? () => onItemAction(item.id) : undefined
          }
          onAffect={
            form.type === EtatDesLieuxTypeEnum.SORTIE && tab === 'new' ? (val) => onAffect(item.id, val) : undefined
          }
          disabled={!accepted && tab === 'new'}
          data={data}
          dataSelected={form.incidentOrganisateurIds.includes(item.id) ? data[0] : data[1]}
        />
      </IncidentContainer>
    )
  }

  const renderBottomHeader = () => (
    <HeaderContainer
      onPress={
        Platform.OS === 'web' ? () => navigation.push(`/edl/spaces/${form.id}?type=${form.type}`) : navigation.goBack
      }
      activeOpacity={0.8}>
      <Icon name="arrow-left" color={theme.colors.primary} />
      <SpaceName>{space && space.libelle}</SpaceName>
    </HeaderContainer>
  )

  const onClickMap = (x: number, y: number) => {
    SimpleAlert.open({
      title: i18n.t('screens.incidentList.map.create'),
      description: i18n.t('screens.incidentList.map.createDescription'),
      buttons: [
        { label: i18n.t('common.cancel'), onClick: () => SimpleAlert.close(), color: 'cancel' },
        {
          label: i18n.t('common.confirm'),
          onClick: () => {
            SimpleAlert.close()
            navigation.push(
              `/incident/start${paramsToSearch({
                manifestationId: form.manifestationId,
                edlId: form.id,
                spaceId,
                x: x.toString(),
                y: y.toString(),
              })}`
            )
          },
        },
      ],
    })
  }

  const renderMap = (path: string, params?: PlanViewerParams) => {
    const initialPosition: ViperViewProps['initialPosition'] | undefined = params
      ? {
          center: params.center ? [params.center.x, params.center.y] : DEFAULT_INIT.center,
          zoom: params.zoom || DEFAULT_INIT.zoom,
        }
      : undefined

    return (
      <MapContainer>
        {!isPhone && (
          <OpenButtonContainer activeOpacity={0.8} onPress={() => setOpen(!open)}>
            <Icon name={open ? 'arrow' : 'arrow-left'} color={theme.colors.primary} />
          </OpenButtonContainer>
        )}

        {!loading && (
          <Viewer
            key={`${path}_${open ? 'open' : 'closed'}`}
            planUrl={path}
            pins={[...pins, ...toSyncPins]}
            // TODO : recevoir ces positions par défaut, sinon le point de départ est arbitraire
            initialPosition={initialPosition || DEFAULT_INIT}
            onClickPin={(pin) => setSelected(pin)}
            onClickMap={onClickMap}
          />
        )}
      </MapContainer>
    )
  }

  const renderIncidents = () => (
    <>
      {form.type === EtatDesLieuxTypeEnum.ENTREE ? (
        <IncidentsNumber>
          {i18n.t('screens.edlMap.incidentsNumber', { incidents: spaceIncidentIds.length })}
        </IncidentsNumber>
      ) : (
        <SegmentedContainer>
          <SegmentedControl
            data={tabs}
            value={tabs.find((t) => t.value === tab)}
            onItemSelected={(opt) => setTab(opt.value)}
            phoneMode
          />
        </SegmentedContainer>
      )}

      <FlatList
        ref={scroll}
        data={[...toSync, ...edlIncidents]}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        contentContainerStyle={{ padding: 16, paddingTop: isPhone ? 32 : 16, paddingBottom: 32 }}
        extraData={form}
        initialScrollIndex={0}
        // le scrollTo ne fonctionnera plus au delà de 200 items dans la liste
        initialNumToRender={200}
        // Permet de prévenir un crash en cas d'erreur de scroll
        onScrollToIndexFailed={() => null}
      />

      {isPhone && (
        <Footer>
          <Button
            label={i18n.t('screens.edlMap.declare')}
            onClick={() =>
              navigation.push(
                `/incident/start${paramsToSearch({
                  manifestationId: form.manifestationId,
                  edlId: form.id,
                  spaceId,
                })}`
              )
            }
            status="active"
          />
        </Footer>
      )}
    </>
  )

  const renderNoPlan = () => (
    <NoPlanContainer>
      <NoPlanText>{i18n.t('screens.map.noPlan')}</NoPlanText>
    </NoPlanContainer>
  )

  return (
    <>
      <Root
        onLeft={navigation.goBack}
        header={space && space.libelle}
        rightText={!isClosed ? i18n.t('screens.edlMap.close') : undefined}
        onRight={isPhone ? () => closeSpace() : undefined}
        fixed>
        {!isPhone && (
          <Header
            title=""
            links={headerLinks}
            renderBottom={renderBottomHeader}
            buttons={!isClosed ? headerButtons : [headerButtons[1]]}
          />
        )}
        <MainContainer>
          {!!plan && !!plan.path ? renderMap(plan.path, plan.params) : renderNoPlan()}
          {!isPhone && open && <IncidentsContainer>{renderIncidents()}</IncidentsContainer>}
        </MainContainer>

        {Platform.OS === 'web' && isPhone && (
          <DrawerContainer pointerEvents="box-none">
            <Drawer render={renderIncidents} sticky onClose={() => null} alreadyOpen offset={130} />
          </DrawerContainer>
        )}
      </Root>
      {Platform.OS !== 'web' && (
        <DrawerContainer pointerEvents="box-none">
          <Drawer render={renderIncidents} sticky onClose={() => null} alreadyOpen offset={100} />
        </DrawerContainer>
      )}
    </>
  )
}

export default EDLMapScreen

const HeaderContainer = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
`

const SpaceName = styled(Text)`
  padding-left: 12px;
  ${(props) => props.theme.fonts.Title2};
  font-size: 20px;
`

const MainContainer = styled(View)`
  flex-direction: row;
  flex: 1;
`

const MapContainer = styled(View)`
  flex: 1;
  background-color: ${(props) => props.theme.colors.background};
`

const IncidentsContainer = styled(View)`
  width: 400px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const OpenButtonContainer = styled(TouchableOpacity)`
  position: absolute;
  right: 24px;
  top: 24px;
  height: 40px;
  width: 40px;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colors.secondaryActive};
  border-radius: 8px;
  z-index: 2;
`

const IncidentContainer = styled(TouchableOpacity)<{ selected: boolean }>`
  margin-bottom: 16px;
  border-radius: 8px;
  border: 3px solid ${(props) => ` ${props.selected ? props.theme.colors.primary : 'transparent'}`};
`

const IncidentsNumber = styled(Text)`
  ${(props) => props.theme.fonts.Callout};
  padding: 16px 16px 0;
  ${(props) => props.theme.breakpoint === 'phone' && 'text-align: center;'}
`

const Footer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`

const SegmentedContainer = styled(View)`
  margin: 0 16px;
  margin-bottom: 16px;
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin-top: 20px;'}
`

const DrawerContainer = styled(View)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`

const NoPlanContainer = styled(View)`
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const NoPlanText = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`
